/**
 * @typedef {"HOUR" | "DAY" | "WEEK" | "MONTH"} EventRepeatInterval
 */

/**
 * @typedef RepeatableEvent
 * @type {object}
 * @property {number} scheduleTime The start time of the event - a unix timestamp with millisecond precision.
 * @property {EventRepeatInterval} repeatInterval How often the event should fire.
 */

export const MILLIS_PER_HOUR = 60 * 60 * 1000;
export const MILLIS_PER_DAY = 24 * MILLIS_PER_HOUR;
export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const SHORT_MONTHS = MONTHS.map(month => month.substring(0, 3));

export function isToday(date) {
  const today = new Date();

  return date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth()
    && date.getDate() === today.getDate();
}

export function isTomorrow(date) {
  const today = new Date();
  today.setDate(today.getDate() +1)
  date = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0 ,0 ,0, 0)
  return date.getTime() === new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0).getTime()
}

export function getFriendlyDateName(date) {
  if (isToday(date)) {
    return 'Today';
  }
  else if (isTomorrow(date)) {
    return 'Tomorrow';
  }

  return `${DAYS[date.getDay()]}, ${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

export function getSuffix(date) {
  const suffixes = ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th'];
  
  //11th, 12th, 13th are special
  if (date === 11 || date === 12 || date === 13) {
    return 'th'
  }
  else {
    return suffixes[date % 10];
  }
}

/** 
 * @param {Date} date The date to look for events.
 * @param {Array<RepeatableEvent>} events The complete events list.
 * @returns The events from the events list that will occur on the given date (24 hour time span).
 */
export function getEventsForDate(date, events) {
  const startTime = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const endTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59).getTime();

  return events
    .filter(event => {
      // If the schedule time happens to be today, regardless if it is repeating or not, then include this event.
      if (event.scheduleTime >= startTime && event.scheduleTime <= endTime) {
        return true;
      }

      // If the schedule time of the event is some time in the past, and the interval is HOUR or DAY, then include
      // this event.
      if (event.scheduleTime <= endTime && (event.repeatInterval === 'HOUR' || event.repeatInterval === 'DAY')) {
        return true;
      }

      // If the schedule time of the event is some time in the past, and the interval is WEEK, then check to see
      // if one of the execution days lands on the current day by calculating the number of days from this date
      // and the schedule time of the event is divisible by 7.
      if (event.scheduleTime <= endTime && event.repeatInterval === 'WEEK') {
        const daysSinceStart = Math.floor((endTime - event.scheduleTime) / MILLIS_PER_DAY);
        return daysSinceStart % 7 === 0;
      }

      // If the schedule time of the event is some time in the past and the interval is MONTH, check to see if the
      // event's scheduled time date matches the current date.
      if (event.scheduleTime <= endTime && event.repeatInterval === 'MONTH') {
        const dayOfMonth = new Date(event.scheduleTime).getDate();
        return dayOfMonth === date.getDate();
      }

      return false;
    });
}

/**
 * @param {Array<RepeatableEvent>} events The complete list of events.
 * @param {Date} [now=new Date()] The date to start the week at.
 * @returns The events from the events list for the next 7 days starting at {@param now}.
 */
export function getWeeklyEvents(events, now = new Date()) {
  return new Array(7).fill(null)
    .map((_, i) => i)
    .map(day => new Date(now.getFullYear(), now.getMonth(), now.getDate() + day))
    .map(date => ({
      date,
      events: getEventsForDate(date, events)
    }));
}

export const DateRangeInterval = {
  MINUTE: {
    difference: 60,
    normalize: date => {
      date.setSeconds(0, 0);
      return date;
    },
    name: date => {
      return `${toNormalHour(date.getHours())}:${leftPad(date.getMinutes(), 2, 0)} ${date.getHours() >= 12 ? 'PM' : 'AM'}`;
    }
  },
  HOUR: {
    difference: 60 * 60,
    normalize: date => {
      date.setMinutes(0, 0, 0);
      return date;
    },
    name: date => {
      return `${toNormalHour(date.getHours())} ${date.getHours() >= 12 ? 'PM' : 'AM'}`;
    }
  },
  DAY: {
    difference: 24 * 60 * 60,
    normalize: date => {
      date.setHours(0, 0, 0, 0);
      return date;
    },
    name: date => {
      return `${SHORT_MONTHS[date.getMonth()]} ${date.getDate()}`;
    }
  }
};

export function getDateRangeInterval(start, end) {
  const diff = end.getTime() - start.getTime();

  if (diff <= MILLIS_PER_DAY / 24) {
    return DateRangeInterval.MINUTE;
  }

  if (diff <= MILLIS_PER_DAY) {
    return DateRangeInterval.HOUR;
  }

  return DateRangeInterval.DAY;
}

export function createDateRange(start, end, type = DateRangeInterval.MINUTE) {
  const range = [];

  // Normalize the start and end dates.
  start = type.normalize(new Date(start));
  end = type.normalize(new Date(end));

  for (let now = end.getTime(); now >= start.getTime(); now -= type.difference * 1000) {
    range.push(new Date(now));
  }

  return range;
}

export function toNormalHour(hour) {
  if (hour === 0) {
    return 12;
  }

  if (hour > 12) {
    return toNormalHour(hour - 12);
  }

  return hour;
}

function leftPad(str, len, char) {
  str = '' + str;

  if (str.length >= len) {
    return str;
  }

  for (let i = 0; i < len - str.length; i++) {
    str = char + str;
  }

  return str;
}